/** @format */

import moment from "moment";
import {
  TAvvisoContrattoSettimana,
  TGiornoPersonaSettimana,
  TPersonaSettimana,
  TTurnoSettimana,
} from "../../../redux-modules/declarations/maps/turniSettimana";

export type TUnita = "ore" | "minuti";

export type TFiltroPersone = "tutti" | "mancate" | "avvisi" | "sotto";

/** Avvisi del contratto mostrati sulle celle e non nella colonna dei totali. */
export const AVVISI_DEL_GIORNO = [
  "CONTRATTO_GIORNO_NON_PREVISTO",
  "CONTRATTO_ORE_GIORNALIERE_SUPERATE",
];
export const AVVISO_ORE_SETTIMANALI = "CONTRATTO_ORE_SETTIMANALI_SUPERATE";

const MENO = "\u2212";

/** Minuti nel formato scelto: ore in centesimi ("42,50") o minuti ("2.550"). */
export const formatMinuti = (minuti: number, unita: TUnita, conSegno = false): string => {
  const assoluto = Math.abs(minuti);
  const testo =
    unita === "ore"
      ? (assoluto / 60).toFixed(2).replace(".", ",")
      : Math.round(assoluto).toLocaleString("it-IT");
  if (minuti < 0) return MENO + testo;
  return conSegno && minuti > 0 ? "+" + testo : testo;
};

/** Ore in centesimi, sempre in ore: per i messaggi ("8,50"). */
export const formatOreDaMinuti = (minuti: number): string => formatMinuti(minuti, "ore");

/** Numero del contratto senza decimali inutili: 40 → "40", 37.5 → "37,5". */
export const formatNumero = (valore: number | null): string => {
  if (valore === null || valore === undefined) return "";
  return Number.isInteger(valore)
    ? String(valore)
    : String(Math.round(valore * 100) / 100).replace(".", ",");
};

export const nomeCompleto = (persona: Pick<TPersonaSettimana, "nome" | "cognome">): string =>
  `${persona.cognome} ${persona.nome}`.trim();

export const iniziali = (persona: Pick<TPersonaSettimana, "nome" | "cognome">): string =>
  ((persona.cognome || "").charAt(0) + (persona.nome || "").charAt(0)).toUpperCase();

/** "08:00–16:30" */
export const orarioTurno = (turno: Pick<TTurnoSettimana, "ora_inizio" | "ora_fine">): string =>
  `${turno.ora_inizio}\u2013${turno.ora_fine}`;

/** Il turno finisce il giorno dopo. */
export const finisceIlGiornoDopo = (
  turno: Pick<TTurnoSettimana, "ora_inizio" | "ora_fine" | "notturno">,
): boolean => turno.notturno || turno.ora_fine < turno.ora_inizio;

/** "2026-10-06 07:58" → "07:58" */
export const ora = (dataOra: string | null): string => (dataOra ? dataOra.substr(11, 5) : "");

/** "2026-10-12 09:30" → "lunedì 12 ottobre 2026 alle 09:30" */
export const dataOraEstesa = (dataOra: string): string =>
  `${moment(dataOra.substr(0, 10)).format("dddd D MMMM YYYY")} alle ${ora(dataOra)}`;

/** Riga sotto l'orario del turno, secondo lo stato. */
export const sottotitoloTurno = (turno: TTurnoSettimana, data: string, oggi: string): string => {
  switch (turno.stato) {
    case "timbrato": {
      if (!turno.timbrature.length) return "Timbrato";
      const prima = turno.timbrature[0];
      const ultima = turno.timbrature[turno.timbrature.length - 1];
      return `${ora(prima.entrata)}\u2013${ora(ultima.uscita)}`;
    }
    case "in_corso": {
      const aperta = turno.timbrature.find(t => t.uscita === null) || turno.timbrature[0];
      return aperta ? `In corso da ${ora(aperta.entrata)}` : "In corso";
    }
    case "mancata_timbratura":
      return data < oggi ? "Mancata timbratura" : "Non timbrato";
    default:
      return "";
  }
};

/** L'avviso riguarda un solo giorno (giorno non previsto, ore giornaliere superate). */
export const eAvvisoDelGiorno = (avviso: Pick<TAvvisoContrattoSettimana, "codice">): boolean =>
  AVVISI_DEL_GIORNO.indexOf(avviso.codice) >= 0;

export type TAmbitoAvvisi = "giorno" | "settimana" | "tutti";

/**
 * Messaggi degli avvisi del contratto che coinvolgono il turno, senza ripetizioni.
 *
 * Gli avvisi della settimana (ore o giorni settimanali superati) elencano tutti i turni della
 * settimana: sul singolo turno si mostrano solo quelli del giorno ("giorno"), gli altri restano
 * nella colonna Settimana e nel dettaglio del turno ("settimana").
 */
export const messaggiAvvisiTurno = (
  turno: Pick<TTurnoSettimana, "id">,
  avvisi: TAvvisoContrattoSettimana[],
  ambito: TAmbitoAvvisi = "tutti",
): string[] =>
  avvisi
    .filter(a => a.shift_planning_ids.indexOf(turno.id) >= 0)
    .filter(a => ambito === "tutti" || eAvvisoDelGiorno(a) === (ambito === "giorno"))
    .map(a => a.messaggio)
    .filter((m, i, tutti) => tutti.indexOf(m) === i);

/** Avvisi della persona che riguardano la settimana nel suo insieme (non un giorno). */
export const avvisiDellaSettimana = (
  avvisi: TAvvisoContrattoSettimana[],
): TAvvisoContrattoSettimana[] =>
  avvisi.filter(
    a => AVVISI_DEL_GIORNO.indexOf(a.codice) < 0 && a.codice !== AVVISO_ORE_SETTIMANALI,
  );

export const haAvvisi = (persona: TPersonaSettimana): boolean =>
  persona.avvisi_contratto.length > 0;

export const sottoContratto = (persona: TPersonaSettimana): boolean =>
  persona.totali.delta_minuti < 0;

export const corrispondeAlFiltro = (
  persona: TPersonaSettimana,
  filtro: TFiltroPersone,
): boolean => {
  switch (filtro) {
    case "mancate":
      return persona.totali.mancate_timbrature > 0;
    case "avvisi":
      return haAvvisi(persona);
    case "sotto":
      return sottoContratto(persona);
    default:
      return true;
  }
};

/** Ricerca per nome, senza distinguere maiuscole e accenti, in qualunque ordine delle parole. */
export const normalizza = (testo: string): string =>
  testo
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export const corrispondeAllaRicerca = (persona: TPersonaSettimana, ricerca: string): boolean => {
  const parole = normalizza(ricerca).split(/\s+/).filter(Boolean);
  if (!parole.length) return true;
  const nome = normalizza(`${persona.cognome} ${persona.nome}`);
  return parole.every(p => nome.indexOf(p) >= 0);
};

export type TStatoTotali = "oltre" | "sotto" | "in_linea";

/**
 * Stato dei totali: oltre il contratto se il delta è positivo o il backend segnala le ore
 * settimanali superate (che si confrontano con il contratto, non con le previste).
 */
export const statoTotali = (deltaMinuti: number, oltreContratto = false): TStatoTotali => {
  if (oltreContratto || deltaMinuti > 0) return "oltre";
  if (deltaMinuti < 0) return "sotto";
  return "in_linea";
};

export const testoDelta = (deltaMinuti: number, unita: TUnita, oltreContratto = false): string => {
  const stato = statoTotali(deltaMinuti, oltreContratto);
  if (stato === "oltre") {
    return deltaMinuti > 0
      ? `${formatMinuti(deltaMinuti, unita, true)} oltre il contratto`
      : "Oltre il contratto";
  }
  if (stato === "sotto") return `Mancano ${formatMinuti(-deltaMinuti, unita)}`;
  return "In linea con il contratto";
};

/** Percentuale della barra: assegnate su previste, al massimo 100. */
export const percentualeBarra = (assegnati: number, previsti: number): number => {
  if (previsti <= 0) return assegnati > 0 ? 100 : 0;
  return Math.min(100, Math.round((assegnati / previsti) * 1000) / 10);
};

/** Descrizione del contratto di una versione: "40 h su 5 giorni". */
export const descrizioneContratto = (ore: number | null, giorni: number | null): string => {
  if (ore === null) return "Contratto senza ore settimanali";
  return giorni
    ? `${formatNumero(ore)} h su ${formatNumero(giorni)} giorni`
    : `${formatNumero(ore)} h`;
};

/** Il giorno ha già un turno lavorativo o un'assenza giornaliera: il "+" diventa piccolo. */
export const giornoOccupato = (giorno: TGiornoPersonaSettimana): boolean =>
  giorno.turni.length > 0 || giorno.assenze.some(a => a.unita === "GIORNI");

/** Colore di fondo leggero da un colore del giustificativo (#RRGGBB), con un ripiego. */
export const tinta = (colore: string | null, alfa = 0.22): string => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec((colore || "").trim());
  if (!m) return `rgba(229, 158, 110, ${alfa})`;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alfa})`;
};
